import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import "./ArenaPage.css";

import DummyTeamDisplay from "../components/arena/DummyTeamDisplay";
import ArenaRecordCard from "../components/arena/ArenaRecordCard";
import ArenaSummaryCard from "../components/arena/ArenaSummaryCard";
import ArenaCharacterEditModal from "../components/arena/ArenaCharacterEditModal";

const Pagination = ({
    currentPage,
    totalItems,
    itemsPerPage,
    onPageChange,
}) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const [jumpToPageInput, setJumpToPageInput] = useState("");

    if (totalPages <= 1) {
        return null;
    }

    const handlePageClick = (page) => {
        if (page >= 1 && page <= totalPages) {
            onPageChange(page);
        }
    };

    const handleJumpToPage = () => {
        const page = parseInt(jumpToPageInput, 10);
        if (!isNaN(page) && page >= 1 && page <= totalPages) {
            onPageChange(page);
            setJumpToPageInput("");
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === "Enter") {
            handleJumpToPage();
        }
    };

    const pageNumbers = [];
    const pageRange = 2;
    for (let i = 1; i <= totalPages; i++) {
        if (
            i === 1 ||
            i === totalPages ||
            (i >= currentPage - pageRange && i <= currentPage + pageRange)
        ) {
            pageNumbers.push(i);
        } else if (
            i === currentPage - pageRange - 1 ||
            i === currentPage + pageRange + 1
        ) {
            pageNumbers.push("...");
        }
    }

    return (
        <div className="pagination-container">
            <button
                className="pagination-button"
                onClick={() => handlePageClick(currentPage - 1)}
                disabled={currentPage === 1}
            >
                &laquo; Prev
            </button>
            {pageNumbers.map((num, index) =>
                num === "..." ? (
                    <span
                        key={`ellipsis-${index}`}
                        className="pagination-ellipsis"
                    >
                        ...
                    </span>
                ) : (
                    <button
                        key={num}
                        className={`pagination-button ${currentPage === num ? "active" : ""}`}
                        onClick={() => handlePageClick(num)}
                    >
                        {num}
                    </button>
                )
            )}
            <button
                className="pagination-button"
                onClick={() => handlePageClick(currentPage + 1)}
                disabled={currentPage === totalPages}
            >
                Next &raquo;
            </button>

            {totalPages > pageRange * 2 + 2 && (
                <div className="pagination-jump-container">
                    <input
                        type="number"
                        className="pagination-jump-input"
                        value={jumpToPageInput}
                        onChange={(e) => setJumpToPageInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        min="1"
                        max={totalPages}
                        placeholder="Page"
                    />
                    <button
                        className="pagination-jump-button"
                        onClick={handleJumpToPage}
                    >
                        Go
                    </button>
                </div>
            )}
        </div>
    );
};

const StatusPopup = ({ message, isError }) => {
    if (!message) return null;
    const baseClasses =
        "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 px-6 py-4 rounded-lg shadow-xl text-sm font-medium z-[100]";
    const successClasses =
        "bg-green-100 border border-green-400 text-green-700";
    const errorClasses = "bg-red-100 border border-red-400 text-red-700";
    return (
        <div
            className={`${baseClasses} ${isError ? errorClasses : successClasses}`}
        >
            {message}
        </div>
    );
};

const ArenaPage = () => {
    const { t, i18n } = useTranslation();
    const [activeTab, setActiveTab] = useState("summaries");
    const [isLoading, setIsLoading] = useState({
        dummy: true,
        records: true,
        summaries: true,
        students: true,
    });

    const [dummyTeam, setDummyTeam] = useState({ main: [], support: [] });
    const [records, setRecords] = useState([]);
    const [summaries, setSummaries] = useState([]);
    const [allStudents, setAllStudents] = useState({});

    const [currentPageRecords, setCurrentPageRecords] = useState(1);
    const [currentPageSummaries, setCurrentPageSummaries] = useState(1);
    const ITEMS_PER_PAGE = 20;

    const [statusMessage, setStatusMessage] = useState({
        text: "",
        isError: false,
    });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCharacter, setEditingCharacter] = useState(null);

    const dragItem = useRef(null);

    const showStatus = (text, isError = false) => {
        setStatusMessage({ text, isError });
        setTimeout(() => setStatusMessage({ text: "", isError: false }), 2500);
    };

    const loadAllData = useCallback(async () => {
        setIsLoading({
            dummy: true,
            records: true,
            summaries: true,
            students: true,
        });
        try {
            const [studentData, dummyData, recordsData, summariesData] =
                await Promise.all([
                    api.getSchaleDBStudents(i18n.language).finally(() =>
                        setIsLoading((prev) => ({
                            ...prev,
                            students: false,
                        }))
                    ),
                    api
                        .getArenaDummy()
                        .finally(() =>
                            setIsLoading((prev) => ({ ...prev, dummy: false }))
                        ),
                    api.getArenaRecords().finally(() =>
                        setIsLoading((prev) => ({
                            ...prev,
                            records: false,
                        }))
                    ),
                    api.getArenaSummaries().finally(() =>
                        setIsLoading((prev) => ({
                            ...prev,
                            summaries: false,
                        }))
                    ),
                ]);

            setAllStudents(studentData);

            const enrichCharacter = (charWrapper) => {
                const studentInfo =
                    studentData[charWrapper.character.uniqueId] || {};
                return {
                    ...charWrapper,
                    character: {
                        ...studentInfo,
                        ...charWrapper.character,
                    },
                };
            };

            const getTeamObjectsByIds = (idArray) => {
                return idArray.map(
                    (id) => studentData[id] || { Id: id, Name: `ID: ${id}` }
                );
            };

            const enrichedDummy = {
                main: dummyData.main.map(enrichCharacter),
                support: dummyData.support.map(enrichCharacter),
            };
            setDummyTeam(enrichedDummy);

            const enrichedRecords = recordsData.map((record) => ({
                ...record,
                attackingTeam: getTeamObjectsByIds(record.attackingTeamIds),
                defendingTeam: getTeamObjectsByIds(record.defendingTeamIds),
            }));
            setRecords(enrichedRecords);

            const enrichedSummaries = summariesData.map((summary) => ({
                ...summary,
                attackingTeam: getTeamObjectsByIds(summary.attackingTeamIds),
                defendingTeam: getTeamObjectsByIds(summary.defendingTeamIds),
            }));
            setSummaries(enrichedSummaries);
        } catch (err) {
            showStatus(`Failed to load arena data: ${err.message}`, true);
            setIsLoading({
                dummy: false,
                records: false,
                summaries: false,
                students: false,
            });
        }
    }, [i18n.language]);

    useEffect(() => {
        loadAllData();
    }, [loadAllData]);

    const lastRecordIndex = currentPageRecords * ITEMS_PER_PAGE;
    const firstRecordIndex = lastRecordIndex - ITEMS_PER_PAGE;
    const currentRecords = records.slice(firstRecordIndex, lastRecordIndex);

    const lastSummaryIndex = currentPageSummaries * ITEMS_PER_PAGE;
    const firstSummaryIndex = lastSummaryIndex - ITEMS_PER_PAGE;
    const currentSummaries = summaries.slice(
        firstSummaryIndex,
        lastSummaryIndex
    );

    const handleDeleteRecord = async (recordToDelete) => {
        if (window.confirm(t("arena.confirmDeleteRecord"))) {
            try {
                const originalRecord = {
                    attackingTeamIds: recordToDelete.attackingTeamIds,
                    defendingTeamIds: recordToDelete.defendingTeamIds,
                    win: recordToDelete.win,
                };
                await api.deleteArenaRecord(originalRecord);
                await loadAllData();
                showStatus(t("arena.recordDeleted"), false);
            } catch (error) {
                showStatus(`Error: ${error.message}`, true);
            }
        }
    };

    const handleDeleteSummary = async (attackingIds, defendingIds) => {
        if (window.confirm(t("arena.confirmDeleteSummary"))) {
            try {
                await api.deleteArenaSummary(attackingIds, defendingIds);
                await loadAllData();
                showStatus(t("arena.summaryDeleted"), false);
            } catch (error) {
                showStatus(`Error: ${error.message}`, true);
            }
        }
    };

    const handleEditCharacter = (charInfo) => {
        setEditingCharacter(charInfo);
        setIsModalOpen(true);
    };

    const handleSaveCharacter = async (updatedCharData) => {
        const studentName =
            updatedCharData.character.Name ||
            `ID: ${updatedCharData.character.uniqueId}`;
        showStatus(`Saving ${studentName}...`);
        try {
            await api.setArenaDummy(updatedCharData);
            await loadAllData();
            showStatus(t("arena.characterSaved"), false);
        } catch (error) {
            showStatus(`Error saving: ${error.message}`, true);
        } finally {
            setIsModalOpen(false);
            setEditingCharacter(null);
        }
    };

    const handleReplaceDummyCharacter = async (
        targetCharInfo,
        newStudentId
    ) => {
        const newStudent = allStudents[newStudentId];
        const studentName = newStudent?.Name || `ID: ${newStudentId}`;
        showStatus(`Replacing with ${studentName}...`);

        try {
            const payload = JSON.parse(JSON.stringify(targetCharInfo));
            payload.character.uniqueId = parseInt(newStudentId, 10);

            await api.setArenaDummy(payload);
            await loadAllData();
            showStatus(t("arena.characterSaved"), false);
        } catch (error) {
            showStatus(`Error saving: ${error.message}`, true);
        }
    };

    const onDragStart = (e, teamType, index) => {
        dragItem.current = { teamType, index };
        e.currentTarget.classList.add("dragging");
    };

    const onDragEnd = (e) => {
        if (e.currentTarget.classList.contains("dragging")) {
            e.currentTarget.classList.remove("dragging");
        }
        dragItem.current = null;
        document
            .querySelectorAll(".drag-over")
            .forEach((el) => el.classList.remove("drag-over"));
    };

    const onDragOver = (e) => {
        e.preventDefault();
        const dropTarget = e.currentTarget;
        if (dropTarget && !dropTarget.classList.contains("drag-over")) {
            document
                .querySelectorAll(".drag-over")
                .forEach((el) => el.classList.remove("drag-over"));
            dropTarget.classList.add("drag-over");
        }
    };

    const onDragLeave = (e) => {
        e.currentTarget.classList.remove("drag-over");
    };

    const onDrop = async (e, targetTeamType, targetIndex) => {
        e.currentTarget.classList.remove("drag-over");
        const source = dragItem.current;

        if (
            !source ||
            source.teamType !== targetTeamType ||
            source.index === targetIndex
        ) {
            return;
        }

        const teamArray = dummyTeam[source.teamType];
        const sourceInfo = teamArray[source.index];
        const targetInfo = teamArray[targetIndex];

        const createSwappedPayload = (infoToMove, slotToFill) => {
            const payload = JSON.parse(JSON.stringify(infoToMove));
            payload.character.serverId = slotToFill.character.serverId;

            if (payload.weapon) {
                payload.weapon.serverId = slotToFill.weapon?.serverId;
            }
            if (payload.gear) {
                payload.gear.serverId = slotToFill.gear?.serverId;
            }
            if (
                payload.equippedEquipment &&
                Array.isArray(payload.equippedEquipment)
            ) {
                payload.equippedEquipment.forEach((equip, i) => {
                    if (equip) {
                        equip.serverId =
                            slotToFill.equippedEquipment?.[i]?.serverId;
                    }
                });
            }

            return payload;
        };

        const payloadForTargetSlot = createSwappedPayload(
            sourceInfo,
            targetInfo
        );

        const payloadForSourceSlot = createSwappedPayload(
            targetInfo,
            sourceInfo
        );

        showStatus(t("arena.savingOrder"), false);
        try {
            await Promise.all([
                api.setArenaDummy(payloadForSourceSlot),
                api.setArenaDummy(payloadForTargetSlot),
            ]);
            showStatus(t("arena.orderSaved"), false);
        } catch (error) {
            showStatus(`Error saving order: ${error.message}`, true);
        } finally {
            await loadAllData();
        }
    };

    const dragHandlers = {
        onDragStart,
        onDragEnd,
        onDragOver,
        onDragLeave,
        onDrop,
    };

    return (
        <>
            <StatusPopup
                message={statusMessage.text}
                isError={statusMessage.isError}
            />

            {isLoading.dummy || isLoading.students ? (
                <p className="text-center text-gray-500 py-8">
                    {t("arena.loadingDummyTeam")}
                </p>
            ) : (
                dummyTeam && (
                    <DummyTeamDisplay
                        dummyTeam={dummyTeam}
                        onEditCharacter={handleEditCharacter}
                        dragHandlers={dragHandlers}
                        onCharacterReplace={handleReplaceDummyCharacter}
                    />
                )
            )}

            {isModalOpen && editingCharacter && (
                <ArenaCharacterEditModal
                    characterData={editingCharacter}
                    allStudentsData={allStudents}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSaveCharacter}
                />
            )}

            <header className="mb-6 md:mb-8">
                <div className="mt-3 sm:mt-4 border-b border-gray-200">
                    <nav
                        className="-mb-px flex space-x-2 sm:space-x-4"
                        aria-label="Tabs"
                    >
                        <button
                            onClick={() => {
                                setActiveTab("summaries");
                                setCurrentPageSummaries(1);
                            }}
                            className={`px-4 py-3 sm:px-6 text-center border-b-2 cursor-pointer hover:text-gray-700 hover:border-gray-300 transition-colors duration-150 ${activeTab === "summaries" ? "text-sky-600 border-sky-500" : "text-black border-transparent"}`}
                        >
                            {t("arena.teamSummaries")}
                        </button>
                        <button
                            onClick={() => {
                                setActiveTab("records");
                                setCurrentPageRecords(1);
                            }}
                            className={`px-4 py-3 sm:px-6 text-center border-b-2 cursor-pointer hover:text-gray-700 hover:border-gray-300 transition-colors duration-150 ${activeTab === "records" ? "text-sky-600 border-sky-500" : "text-black border-transparent"}`}
                        >
                            {t("arena.battleRecords")}
                        </button>
                    </nav>
                </div>
            </header>

            <main>
                {activeTab === "summaries" && (
                    <div>
                        {isLoading.summaries ? (
                            <p className="text-center text-gray-500 py-8">
                                {t("arena.loadingSummaries")}
                            </p>
                        ) : summaries.length === 0 ? (
                            <p className="text-center text-gray-500 py-8">
                                {t("arena.noSummaries")}
                            </p>
                        ) : (
                            <>
                                {currentSummaries.map((summary, index) => (
                                    <ArenaSummaryCard
                                        key={`${summary.attackingTeamIds.join()}-${summary.defendingTeamIds.join()}-${index}`}
                                        summary={summary}
                                        onDelete={handleDeleteSummary}
                                    />
                                ))}
                                <Pagination
                                    currentPage={currentPageSummaries}
                                    totalItems={summaries.length}
                                    itemsPerPage={ITEMS_PER_PAGE}
                                    onPageChange={(page) =>
                                        setCurrentPageSummaries(page)
                                    }
                                />
                            </>
                        )}
                    </div>
                )}
                {activeTab === "records" && (
                    <div>
                        {isLoading.records ? (
                            <p className="text-center text-gray-500 py-8">
                                {t("arena.loadingRecords")}
                            </p>
                        ) : records.length === 0 ? (
                            <p className="text-center text-gray-500 py-8">
                                {t("arena.noRecords")}
                            </p>
                        ) : (
                            <>
                                {currentRecords.map((record, index) => (
                                    <ArenaRecordCard
                                        key={`${record.attackingTeamIds.join()}-${record.defendingTeamIds.join()}-${index}`}
                                        record={record}
                                        onDelete={handleDeleteRecord}
                                    />
                                ))}
                                <Pagination
                                    currentPage={currentPageRecords}
                                    totalItems={records.length}
                                    itemsPerPage={ITEMS_PER_PAGE}
                                    onPageChange={(page) =>
                                        setCurrentPageRecords(page)
                                    }
                                />
                            </>
                        )}
                    </div>
                )}
            </main>
        </>
    );
};

export default ArenaPage;
